// Client-side HTTPS enforcement for the PitchVault SPA.
//
// The site is served over HTTPS in production, but a visitor can still land on
// the plain-http origin (e.g. an old bookmark, a typed URL, or a link that
// omits the scheme). This module upgrades those loads to HTTPS before the app
// boots, complementing the `upgrade-insecure-requests` CSP directive in
// public/index.html that upgrades sub-resource requests.
//
// Local development and loopback hosts are left untouched — there is no TLS
// listener there and forcing HTTPS would just break the dev server.

/**
 * The subset of `window.location` this module reads. Kept structural so tests
 * can pass a plain object and a real `Location` satisfies it.
 */
export interface LocationLike {
  protocol?: string;
  hostname?: string;
  href?: string;
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

// A host is considered local (and therefore exempt) when it is a loopback
// address or a *.local mDNS name.
function isLocalHost(hostname: string | undefined): boolean {
  if (!hostname) {
    return false;
  }
  return LOOPBACK_HOSTS.has(hostname) || hostname.endsWith('.local');
}

/**
 * Given a location-like object, return the HTTPS URL the browser should be
 * redirected to, or null when no redirect is needed.
 */
export function httpsRedirectTarget(location: LocationLike | null | undefined): string | null {
  if (!location || location.protocol !== 'http:' || isLocalHost(location.hostname)) {
    return null;
  }
  if (typeof location.href !== 'string') {
    return null;
  }
  return location.href.replace(/^http:/i, 'https:');
}

/**
 * Redirect the current window to its HTTPS equivalent when it was loaded over
 * plain HTTP on a non-local host. Safe to call on the server (no-op) and in
 * environments without a window.
 */
export function enforceHttps(
  win: Window | undefined = typeof window !== 'undefined' ? window : undefined
): void {
  if (!win || !win.location) {
    return;
  }
  const target = httpsRedirectTarget(win.location);
  if (target) {
    win.location.replace(target);
  }
}
